import { createSlice } from '@reduxjs/toolkit'
import { apiCallBegan } from '../api'
import {
  incidentFeedInitialState as initialState,
  INCIDENT_FEED_BUCKET_COUNT,
} from '../initialState/incidentFeedState'

// ===========================================================================
// Incident Feed → Overview (PIXMC-6521).
//
// Three GETs against pixm-dashboard-fa, all customer-scoped:
//   dashboard/stats/{id}/overview/phishing-detections — donut counts   (PIXMC-6520 WS-1, new)
//   dashboard/stats/{id}/phishing-detections          — bucketed series (PIXMC-6520 WS-2, new)
//   dashboard/stats/{id}/events                       — pages scanned   (existing, unchanged)
//
// ⚠️ The donut and the chart are two DIFFERENT routes whose paths now differ only by the
// `overview/` prefix. The donut is the one nested under `overview/`. They count
// different things — do not collapse them.
//
// ⚠️ The two NEW routes take short param names (st / et / bcktCnt); the existing /events
// route keeps startTime / endTime / numberOfBuckets. The portal therefore sends both
// conventions on one screen. That is deliberate on the API side, not an oversight here —
// see the field map in .claude/docs/plan-IncidentFeedOverview.md.
//
// ⚠️ All three use the dashboard FA's house envelope: `{ data, errors }` returned with
// HTTP **200 even on failure**. A server-side error therefore arrives through onSuccess
// with a populated `errors` array, NOT through onError — every *Received reducer below
// checks `errors` first. onError still fires for transport/auth failures.
// ===========================================================================

// The two helpers below extract a human-readable detail from whatever the failure looked
// like. 🔴 That detail is for the console and Redux DevTools ONLY — it lands on
// `<thing>Error` in state and must never be rendered. The UI shows the fixed LOAD_ERROR
// string from components/IncidentFeed/messages.js instead, so "Request failed with status
// code 401" can never reach a customer admin's screen.
//
// `errors` entries are { code, desc } (shared/src/scripts/utils.js addError).
const envelopeError = (response) => {
  const errors = response?.errors
  if (!Array.isArray(errors) || errors.length === 0) return null
  return errors[0].desc || errors[0].message || errors[0].code || 'Unknown error'
}

// A response is stale when the customer it was issued for is no longer the customer the
// slice is waiting on — i.e. the user switched while it was in flight. Without this the
// last response to land wins, which is "intermittently wrong numbers" rather than a clean
// failure. `reducerData` is forwarded to onStart/onSuccess/onError by the api middleware.
const isStale = (state, action, key) =>
  action.reducerData?.customerId !== state[`${key}RequestedFor`]

const extractError = (payload) => {
  if (payload?.errors?.length > 0) {
    return payload.errors[0].desc || payload.errors[0].message || 'Unknown error'
  }
  if (payload?.message) return payload.message
  if (typeof payload === 'string') return payload
  return 'Unknown error'
}

const slice = createSlice({
  name: 'incidentFeed',
  initialState,
  reducers: {
    // ----- Donut -----
    overviewRequested: (state, action) => {
      state.overviewLoading = true
      state.overviewError = null
      state.overviewRequestedFor = action.reducerData?.customerId ?? null
    },
    overviewReceived: (state, action) => {
      if (isStale(state, action, 'overview')) return
      state.overviewLoading = false
      const response = action.payload.response
      const error = envelopeError(response)
      if (error) {
        state.overviewError = error
        state.overview = { ...initialState.overview, available: false }
        return
      }
      const data = response?.data ?? {}
      // D-7: `available` drives the branch, never the counts — 0 is falsy in JS, and a
      // genuine zero must render a ring at zero while "not yet computed" must not.
      // Default to false (not true) so a malformed payload reads as "no data" rather
      // than as a reassuring clean ring.
      state.overview = {
        available: data.available === true,
        pixmVerified: data.pixmVerified ?? null,
        verifiedPhish: data.verifiedPhish ?? null,
        aiSuspicious: data.aiSuspicious ?? null,
        total: data.total ?? null,
        lookbackDays: data.lookbackDays ?? null,
        computedAt: data.computedAt ?? null,
      }
    },
    overviewFailed: (state, action) => {
      if (isStale(state, action, 'overview')) return
      state.overviewLoading = false
      state.overviewError = extractError(action.payload)
      state.overview = { ...initialState.overview, available: false }
    },

    // ----- Phishing detections series -----
    phishingDetectionsRequested: (state, action) => {
      state.phishingDetectionsLoading = true
      state.phishingDetectionsError = null
      state.phishingDetectionsRequestedFor = action.reducerData?.customerId ?? null
    },
    phishingDetectionsReceived: (state, action) => {
      if (isStale(state, action, 'phishingDetections')) return
      state.phishingDetectionsLoading = false
      const response = action.payload.response
      const error = envelopeError(response)
      if (error) {
        state.phishingDetectionsError = error
        state.phishingDetections = { ...initialState.phishingDetections, available: false }
        return
      }
      const data = response?.data ?? {}
      const buckets = Array.isArray(data.rcds) ? data.rcds : []
      state.phishingDetections = {
        available: data.available === true,
        // Normalise { dtcTime, rcds } → { detectionTime, records } so TrendPanel reads one
        // shape for both panels. `rcds` names both the array and each bucket's count on
        // the wire; that ambiguity stops at this line.
        records: buckets.map((b) => ({
          detectionTime: b.dtcTime,
          records: b.rcds,
        })),
        totalRecords: data.ttlRcds ?? 0,
        lookbackDays: data.lookbackDays ?? null,
        computedAt: data.computedAt ?? null,
      }
    },
    phishingDetectionsFailed: (state, action) => {
      if (isStale(state, action, 'phishingDetections')) return
      state.phishingDetectionsLoading = false
      state.phishingDetectionsError = extractError(action.payload)
      state.phishingDetections = { ...initialState.phishingDetections, available: false }
    },

    // ----- Pages scanned -----
    // Same endpoint and payload as the Chart View's Login Pages Scanned, read into this
    // slice rather than events.loginList so the two can hold different bucket counts
    // (30 here, 9 there) without one screen clobbering the other's data.
    pagesScannedRequested: (state, action) => {
      state.pagesScannedLoading = true
      state.pagesScannedError = null
      state.pagesScannedRequestedFor = action.reducerData?.customerId ?? null
    },
    pagesScannedReceived: (state, action) => {
      if (isStale(state, action, 'pagesScanned')) return
      state.pagesScannedLoading = false
      const response = action.payload.response
      const error = envelopeError(response)
      if (error) {
        state.pagesScannedError = error
        state.pagesScanned = { ...initialState.pagesScanned }
        return
      }
      const data = response?.data ?? {}
      const records = Array.isArray(data.records) ? data.records : []
      state.pagesScanned = {
        records,
        // Mirrors loginPagesScannedEventsReceived: the total is only meaningful alongside
        // buckets, so an empty series reports 0 rather than a stale count.
        totalRecords: records.length > 0 ? data.totalRecords ?? 0 : 0,
      }
    },
    pagesScannedFailed: (state, action) => {
      if (isStale(state, action, 'pagesScanned')) return
      state.pagesScannedLoading = false
      state.pagesScannedError = extractError(action.payload)
      state.pagesScanned = { ...initialState.pagesScanned }
    },

    resetIncidentFeed: () => initialState,
  },
})

export const {
  overviewRequested, overviewReceived, overviewFailed,
  phishingDetectionsRequested, phishingDetectionsReceived, phishingDetectionsFailed,
  pagesScannedRequested, pagesScannedReceived, pagesScannedFailed,
  resetIncidentFeed,
} = slice.actions

export default slice.reducer

// ===========================================================================
// Thunks
// ===========================================================================

// The new routes' st/et are plain YYYY-MM-DD. Their WHERE is
// `ag.[Date] BETWEEN @StartDate AND @EndDate` over a DATE column, so a timestamp on
// @StartDate would drop the whole first day. getUTCDate() returns
// 'YYYY-MM-DD HH:mm:ss.SSS'; slicing to 10 is the whole conversion.
const toDateParam = (value) => (typeof value === 'string' ? value.slice(0, 10) : value)

// Global-view branch, identical to the one every other dashboard thunk uses
// (events.js loadTopThreats / loadLoginPagesScannedEvents): r=C is a single customer,
// r=M is the MSP aggregate and must carry the tenant's defaultCustomerId.
const withGlobalView = (filters, defaultCustomerId) =>
  filters?.r === 'M' ? { ...filters, customerId: defaultCustomerId } : filters

const baseFilters = ({ startTime, endTime, isGlobalView, cKey }) => ({
  st: toDateParam(startTime),
  et: toDateParam(endTime),
  r: isGlobalView ? 'M' : 'C',
  cKey,
})

// GET the three donut counts for the window. No series — the donut and the chart count
// different things and deliberately do not share a payload.
export const getOverview = (customerId, options, defaultCustomerId) => apiCallBegan({
  reducerData: { customerId },
  urlPath: `dashboard/stats/${customerId}/overview/phishing-detections`,
  method: 'GET',
  filters: withGlobalView(baseFilters(options), defaultCustomerId),
  onStart: overviewRequested.type,
  onSuccess: overviewReceived.type,
  onError: overviewFailed.type,
})

// GET the single aggregated phishing series (PIXM Verified + AI Suspicious + suspicious
// Google Forms; Verified Phish excluded). Its total is therefore LOWER than the donut's
// by exactly verifiedPhish — by design, not a defect.
export const getPhishingDetections = (customerId, options, defaultCustomerId) => apiCallBegan({
  reducerData: { customerId },
  urlPath: `dashboard/stats/${customerId}/phishing-detections`,
  method: 'GET',
  filters: withGlobalView(
    { ...baseFilters(options), bcktCnt: INCIDENT_FEED_BUCKET_COUNT },
    defaultCustomerId,
  ),
  onStart: phishingDetectionsRequested.type,
  onSuccess: phishingDetectionsReceived.type,
  onError: phishingDetectionsFailed.type,
})

// GET pages scanned from the EXISTING endpoint — long param names, full timestamps,
// contract untouched. Only numberOfBuckets differs from the Chart View's call (30 vs 9);
// totalRecords is computed before bucketing and is identical either way, which is the
// parity check PIXMC-6519 gates on.
export const getPagesScanned = (customerId, options, defaultCustomerId) => {
  const { startTime, endTime, isGlobalView, cKey } = options
  return apiCallBegan({
    reducerData: { customerId },
    urlPath: `dashboard/stats/${customerId}/events`,
    method: 'GET',
    filters: withGlobalView(
      {
        startTime,
        endTime,
        requireTotalRecordsCount: 1,
        numberOfBuckets: INCIDENT_FEED_BUCKET_COUNT,
        r: isGlobalView ? 'M' : 'C',
        cKey,
      },
      defaultCustomerId,
    ),
    onStart: pagesScannedRequested.type,
    onSuccess: pagesScannedReceived.type,
    onError: pagesScannedFailed.type,
  })
}
